/*
  [04] Wishlist
  One owner for every heart on the site:
  - stores product handles in localStorage ('nd-wishlist', shared with the Nude carousels)
  - keeps every [data-nd-wish] button in sync (aria-pressed)
  - updates the header counters [data-nd-wl-count]
  - opens the wishlist panel from [data-nd-wl-open]; cards are rendered by the
    'nd-wishlist-card' section so prices follow the [04] reverse price engine.
*/
(() => {
  if (window.NdWishlist && window.NdWishlist.__v04) return;

  const STORE_KEY = 'nd-wishlist';
  const CARD_SECTION = 'nd-wishlist-card';
  const root = (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || '/';
  const cardCache = new Map();
  let items = null;

  const read = () => {
    if (items) return items;
    items = [];
    try {
      const parsed = JSON.parse(window.localStorage.getItem(STORE_KEY) || '[]');
      if (Array.isArray(parsed)) items = parsed.filter((h) => typeof h === 'string' && h.length);
    } catch (error) {
      items = [];
    }
    return items;
  };

  const write = () => {
    try {
      window.localStorage.setItem(STORE_KEY, JSON.stringify(items || []));
    } catch (error) {
      /* Storage blocked: the list still works for this visit. */
    }
  };

  /* ---------- live region ---------- */
  let live = null;
  const announce = (message) => {
    if (!live) {
      live = document.createElement('div');
      live.className = 'nd-wl-live';
      live.setAttribute('aria-live', 'polite');
      live.setAttribute('role', 'status');
      document.body.appendChild(live);
    }
    live.textContent = '';
    window.setTimeout(() => {
      live.textContent = message;
    }, 60);
  };

  /* ---------- sync ---------- */
  const syncButtons = (scope) => {
    (scope || document).querySelectorAll('[data-nd-wish]').forEach((button) => {
      const handle = button.getAttribute('data-nd-wish');
      if (!handle) return;
      const saved = read().includes(handle);
      button.setAttribute('aria-pressed', String(saved));
      const title = button.getAttribute('data-nd-wish-title') || 'product';
      button.setAttribute('aria-label', `${saved ? 'Remove' : 'Save'} ${title} ${saved ? 'from' : 'to'} SAVED`);
    });
  };

  const syncCounts = () => {
    const count = read().length;
    document.querySelectorAll('[data-nd-wl-count]').forEach((el) => {
      el.textContent = count > 99 ? '99+' : String(count);
      el.hidden = count === 0;
    });
    document.querySelectorAll('[data-nd-wl-open]').forEach((el) => {
      el.setAttribute('aria-label', count ? `SAVED, ${count} products` : 'SAVED');
      el.toggleAttribute('data-has-items', count > 0);
    });
  };

  const syncAll = (scope) => {
    syncButtons(scope);
    syncCounts();
  };

  const setSaved = (handle, saved) => {
    const list = read();
    const index = list.indexOf(handle);
    if (saved && index === -1) list.unshift(handle);
    if (!saved && index !== -1) list.splice(index, 1);
    write();
    syncAll();
    document.dispatchEvent(new CustomEvent('nd:wishlist:change', { detail: { handle, saved, count: list.length } }));
    return saved;
  };

  /* ---------- panel ---------- */
  let dialog = null;
  let opener = null;
  let renderToken = 0;
  let mossLayer = null;
  let mossSize = '';
  let mossFrame;

  const paintMoss = (animate = false) => {
    const host = dialog?.querySelector('[data-nd-wl-moss]');
    const engine = window.Moss04Engine;
    if (!dialog?.open || !host || !engine) return;
    const size = `${host.clientWidth}:${host.clientHeight}`;
    if (!host.clientWidth || !host.clientHeight) return;
    const config = window.__moss04 || {};
    if (!mossLayer) mossLayer = new engine.Layer(host, {
      lut: engine.makeLUT(config.palette),
      size: config.size || 1,
      dir: 'down',
      anchor: 'top',
      profile: (position) => 0.55 + 0.35 * engine.ss(0, 1, position),
    });
    if (!animate && size === mossSize) return;
    cancelAnimationFrame(mossLayer.raf);
    mossLayer.build();
    mossSize = size;
    const strength = Math.min(1, config.grow ?? 1);
    if (animate && config.animate !== false && !matchMedia('(prefers-reduced-motion: reduce)').matches) {
      mossLayer.paint(0, 0);
      mossLayer.grow(strength, 1800);
    } else mossLayer.paint(strength);
  };

  const buildDialog = () => {
    if (dialog) return dialog;
    dialog = document.createElement('dialog');
    dialog.className = 'nd-wl';
    dialog.setAttribute('aria-labelledby', 'NdWishlistTitle');
    dialog.innerHTML = `
      <div class="nd-wl__panel">
        <div class="nd-wl__moss" data-nd-wl-moss aria-hidden="true"></div>
        <div class="nd-wl__head">
          <h2 class="nd-wl__title" id="NdWishlistTitle">SAVED <span class="nd-wl__count" data-nd-wl-count hidden></span></h2>
          <button type="button" class="nd-wl__close" data-nd-wl-close aria-label="Close SAVED">
            <svg width="16" height="16" viewBox="0 0 16 16" aria-hidden="true" focusable="false"><path d="M3 3l10 10M13 3 3 13" stroke="currentColor" stroke-width="1.4" stroke-linecap="round"/></svg>
          </button>
        </div>
        <div class="nd-wl__body">
          <ul class="nd-wl__grid" role="list" data-nd-wl-grid></ul>
          <div class="nd-wl__empty" data-nd-wl-empty hidden>
            <p class="nd-wl__empty-title">Nothing saved yet</p>
            <p class="nd-wl__empty-text">Tap the star on any piece to save it here.</p>
            <a class="nd-wl__cta" href="${root}collections/all">Shop all</a>
          </div>
        </div>
      </div>`;
    document.body.appendChild(dialog);
    const mossObserver = new ResizeObserver(() => paintMoss());
    mossObserver.observe(dialog);
    window.addEventListener('load', () => paintMoss(), { once: true });

    dialog.addEventListener('click', (event) => {
      if (event.target === dialog) close();
      if (event.target.closest('[data-nd-wl-close]')) close();
    });
    dialog.addEventListener('close', () => {
      cancelAnimationFrame(mossFrame);
      if (mossLayer) cancelAnimationFrame(mossLayer.raf);
      document.documentElement.classList.remove('nd-wl-lock');
      if (opener && typeof opener.focus === 'function') opener.focus();
      opener = null;
    });
    syncCounts();
    return dialog;
  };

  const runScripts = (scope) => {
    scope.querySelectorAll('script').forEach((old) => {
      const script = document.createElement('script');
      for (const attr of old.attributes) script.setAttribute(attr.name, attr.value);
      script.textContent = old.textContent;
      old.replaceWith(script);
    });
  };

  const fetchCard = async (handle) => {
    if (cardCache.has(handle)) return cardCache.get(handle);
    const url = `${root}products/${encodeURIComponent(handle)}?section_id=${CARD_SECTION}`;
    const promise = fetch(url, { credentials: 'same-origin' })
      .then((response) => {
        if (response.status === 404) return null;
        if (!response.ok) throw new Error(String(response.status));
        return response.text();
      })
      .then((html) => {
        if (!html) return null;
        const doc = new DOMParser().parseFromString(html, 'text/html');
        const card = doc.querySelector('.nd-wl-card');
        return card ? card.outerHTML : null;
      })
      .catch(() => {
        cardCache.delete(handle);
        return undefined;
      });
    cardCache.set(handle, promise);
    return promise;
  };

  const updateEmpty = () => {
    if (!dialog) return;
    const grid = dialog.querySelector('[data-nd-wl-grid]');
    const empty = dialog.querySelector('[data-nd-wl-empty]');
    const hasCards = grid.querySelector('.nd-wl__item') !== null;
    empty.hidden = hasCards || read().length > 0;
  };

  const render = async () => {
    const token = ++renderToken;
    const grid = dialog.querySelector('[data-nd-wl-grid]');
    const handles = read().slice();
    grid.innerHTML = handles
      .map((h) => `<li class="nd-wl__item is-loading" data-nd-wl-item="${h.replace(/"/g, '')}"><div class="nd-wl__skeleton"></div></li>`)
      .join('');
    updateEmpty();

    await Promise.all(
      handles.map(async (handle) => {
        const html = await fetchCard(handle);
        if (token !== renderToken) return;
        const li = grid.querySelector(`[data-nd-wl-item="${CSS.escape(handle)}"]`);
        if (!li) return;
        if (html === null) {
          /* Product no longer exists: drop it from the list. */
          li.remove();
          setSaved(handle, false);
          return;
        }
        if (html === undefined) {
          li.classList.remove('is-loading');
          li.innerHTML = `<a class="nd-wl__fallback" href="${root}products/${encodeURIComponent(handle)}">${handle.replace(/-/g, ' ')}</a>`;
          return;
        }
        li.classList.remove('is-loading');
        li.innerHTML = html;
        runScripts(li);
        syncButtons(li);
      })
    );
    if (token === renderToken) updateEmpty();
  };

  const open = (trigger) => {
    buildDialog();
    const menuDrawer = trigger?.closest('[data-nav04-drawer]');
    opener = menuDrawer ? menuDrawer.querySelector(':scope > summary') : (trigger || document.activeElement);
    if (menuDrawer) menuDrawer.open = false;
    if (!dialog.open) {
      if (typeof dialog.showModal === 'function') dialog.showModal();
      else dialog.setAttribute('open', '');
    }
    document.documentElement.classList.add('nd-wl-lock');
    cancelAnimationFrame(mossFrame);
    mossFrame = requestAnimationFrame(() => paintMoss(true));
    render();
    const closeButton = dialog.querySelector('[data-nd-wl-close]');
    if (closeButton) closeButton.focus();
  };

  const close = () => {
    if (!dialog) return;
    if (typeof dialog.close === 'function' && dialog.open) dialog.close();
    else {
      dialog.removeAttribute('open');
      dialog.dispatchEvent(new Event('close'));
    }
  };

  /* ---------- events ---------- */
  document.addEventListener(
    'click',
    (event) => {
      const heart = event.target.closest('[data-nd-wish]');
      if (heart) {
        /* Capture phase: keeps product-card link and view-transition handlers from firing. */
        event.preventDefault();
        event.stopPropagation();
        const handle = heart.getAttribute('data-nd-wish');
        if (!handle) return;
        const saved = setSaved(handle, !read().includes(handle));
        heart.classList.remove('is-pop');
        void heart.offsetWidth;
        if (saved) heart.classList.add('is-pop');
        const title = heart.getAttribute('data-nd-wish-title') || 'Product';
        announce(saved ? `${title} added to SAVED` : `${title} removed from SAVED`);
        if (!saved && dialog && dialog.contains(heart)) {
          const li = heart.closest('.nd-wl__item');
          if (li) li.remove();
          updateEmpty();
        }
        return;
      }
      const trigger = event.target.closest('[data-nd-wl-open]');
      if (trigger) {
        event.preventDefault();
        open(trigger);
      }
    },
    true
  );

  window.addEventListener('storage', (event) => {
    if (event.key !== STORE_KEY) return;
    items = null;
    syncAll();
    if (dialog && dialog.open) render();
  });

  document.addEventListener('shopify:section:load', (event) => syncAll(event.target));

  window.NdWishlist = {
    __v04: true,
    list: () => read().slice(),
    has: (handle) => read().includes(handle),
    toggle: (handle) => setSaved(handle, !read().includes(handle)),
    add: (handle) => setSaved(handle, true),
    remove: (handle) => setSaved(handle, false),
    sync: (scope) => syncAll(scope),
    reload: () => {
      items = null;
    },
    open,
    close,
  };

  const init = () => syncAll(document);
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', init, { once: true });
  else init();

  /* Product grids that load more cards later (pagination, filters, quick add). */
  if ('MutationObserver' in window) {
    let pending = false;
    new MutationObserver(() => {
      if (pending) return;
      pending = true;
      requestAnimationFrame(() => {
        pending = false;
        syncButtons(document);
      });
    }).observe(document.documentElement, { childList: true, subtree: true });
  }
})();
